/**
 * LLM Connections
 *
 * Named provider configurations that users can add, configure, and switch between.
 * Each session locks to a specific connection after the first message.
 * Workspaces can set a default connection.
 */

// Import model types and lists from centralized registry
// NOTE: Pi SDK functions (getPiModelsForAuthProvider, getAllPiModels) are NOT imported
// here because @mariozechner/pi-ai transitively pulls in @aws-sdk which uses Node.js
// `stream` module — breaking the Vite renderer build. Instead, Pi model resolution is
// injected at app startup via registerPiModelResolver().
import {
  type ModelDefinition,
  ANTHROPIC_MODELS,
  DEFAULT_MODEL,
  getModelById,
  getModelDisplayName,
} from './models';
import type { CredentialManager } from '../credentials/manager.ts';
import type { ReasoningEffort, ThinkingLevel } from '../agent/thinking-levels.ts';

// ============================================================
// Pi Model Resolver (dependency injection to avoid Pi SDK in renderer)
// ============================================================

type PiModelResolver = (piAuthProvider?: string) => ModelDefinition[];
let _piModelResolver: PiModelResolver = () => [];

/**
 * Register the Pi model resolver function.
 * Must be called from main process at app startup (before any Pi connections are used).
 * This avoids pulling @mariozechner/pi-ai into the renderer bundle.
 */
export function registerPiModelResolver(resolver: PiModelResolver): void {
  _piModelResolver = resolver;
}

// ============================================================
// Types
// ============================================================

/**
 * Provider type determines which backend/SDK implementation to use.
 * This is separate from auth mechanism - a provider may support multiple auth types.
 *
 * - 'anthropic': Direct Anthropic API (api.anthropic.com)
 * - 'anthropic_compat': Anthropic-format compatible endpoints (OpenRouter, etc.)
 * - 'bedrock': AWS Bedrock (Claude models via AWS)
 * - 'vertex': Google Vertex AI (Claude models via GCP)
 * - 'pi': Pi unified LLM API (20+ providers via @mariozechner/pi-ai)
 * - 'pi_compat': Pi with custom endpoint (Ollama, self-hosted models)
 */
export type LlmProviderType =
  | 'anthropic'
  | 'anthropic_compat'
  | 'bedrock'
  | 'vertex'
  | 'pi'
  | 'pi_compat';

/**
 * @deprecated Use LlmProviderType instead. Kept for migration compatibility.
 */
export type LlmConnectionType = 'anthropic' | 'openai' | 'openai-compat';

/**
 * Authentication mechanism for the connection.
 * Determines the UI pattern, credential storage format, and how credentials are passed.
 *
 * Simple token auth:
 * - 'api_key': Single API key field, fixed endpoint known
 * - 'api_key_with_endpoint': API key + custom endpoint URL fields
 * - 'bearer_token': Single bearer token (different header than API key)
 *
 * OAuth flows (browser redirect):
 * - 'oauth': Browser OAuth flow, provider determined by providerType
 *
 * Cloud provider auth:
 * - 'iam_credentials': AWS-style (Access Key + Secret Key + Region)
 * - 'service_account_file': GCP-style JSON file upload
 * - 'environment': Auto-detect from environment variables
 *
 * No auth:
 * - 'none': No authentication required (local models like Ollama)
 */
export type LlmAuthType =
  | 'api_key'
  | 'api_key_with_endpoint'
  | 'oauth'
  | 'iam_credentials'
  | 'bearer_token'
  | 'service_account_file'
  | 'environment'
  | 'none';

/**
 * Ownership mode for a connection's model list.
 * - automaticallySyncedFromProvider: provider defaults are synced automatically.
 * - userDefined3Tier: user-picked Best/Balanced/Fast list is preserved.
 */
export type ModelSelectionMode = 'automaticallySyncedFromProvider' | 'userDefined3Tier';

/**
 * LLM Connection configuration.
 * Stored in config.llmConnections array.
 */
export interface LlmConnection {
  /** URL-safe identifier (e.g., 'anthropic-api', 'ollama-local') */
  slug: string;

  /** Display name shown in UI (e.g., 'Anthropic (API Key)', 'Ollama') */
  name: string;

  /** Provider type determines backend/SDK implementation */
  providerType: LlmProviderType;

  /**
   * @deprecated Use providerType instead. Kept for migration compatibility.
   * Will be removed in a future version.
   */
  type?: LlmConnectionType;

  /** Custom base URL (required for *_compat providers, optional override for others) */
  baseUrl?: string;

  /** Authentication mechanism */
  authType: LlmAuthType;

  /** Override available models (for custom endpoints that don't support model listing) */
  models?: Array<ModelDefinition | string>;

  /** Default model for this connection */
  defaultModel?: string;

  /**
   * Ownership mode for the model list.
   * - automaticallySyncedFromProvider: provider defaults are kept in sync.
   * - userDefined3Tier: preserve user-selected Best/Balanced/Fast list.
   */
  modelSelectionMode?: ModelSelectionMode;

  /**
   * Pi auth provider name (e.g., 'anthropic', 'openai', 'github-copilot').
   * Determines which provider credential Pi SDK uses for LLM calls.
   * Only relevant for 'pi' providerType connections.
   */
  piAuthProvider?: string;

  // --- Cloud provider specific fields ---

  /** AWS region (for 'bedrock' provider) */
  awsRegion?: string;

  /** GCP project ID (for 'vertex' provider) */
  gcpProjectId?: string;

  /** GCP region (for 'vertex' provider, e.g., 'us-central1') */
  gcpRegion?: string;

  // --- Timestamps ---

  /** Timestamp when connection was created */
  createdAt: number;

  /** Timestamp when connection was last used */
  lastUsedAt?: number;

  /** Origin-managed connection backed by credentials bundled with the app */
  isBundled?: boolean;
}

/**
 * LLM Connection with authentication status.
 * Used by UI to show which connections are ready to use.
 */
export interface LlmConnectionWithStatus extends LlmConnection {
  /** Whether the connection has valid credentials */
  isAuthenticated: boolean;

  /** Error message if authentication check failed */
  authError?: string;

  /** Whether this is the global default connection */
  isDefault?: boolean;
}

/**
 * Curated, Origin-managed model presets backed by API keys bundled with the app.
 *
 * These definitions are shared so desktop, remote, and server code agree on
 * the connection/model/thinking-level tuple selected before a session starts.
 */
export type ManagedModelPresetId = 'gpt-5.6-sol' | 'gpt-5.6-luna' | 'gpt-6-astra' | 'opus-5' | 'fable-5';

export type ManagedReasoningEffort = ReasoningEffort;

export interface ManagedUtilityModelProfile {
  model: string;
  thinkingLevel: ThinkingLevel;
  effort: ManagedReasoningEffort;
}

export interface ManagedModelPreset {
  id: ManagedModelPresetId;
  label: string;
  providerLabel: string;
  connectionSlug: string;
  model: string;
  thinkingLevel: ThinkingLevel;
  effortLabel: 'high' | 'xhigh' | 'max';
  /** Single-turn, no-tools model used by call_llm and title generation. */
  completion: ManagedUtilityModelProfile;
  /** Hidden utility model used by focused mini edit sessions. Spawned sessions always use Opus 5.5 instead. */
  implementation: ManagedUtilityModelProfile;
  isDefault?: boolean;
  connection: Omit<LlmConnection, 'createdAt' | 'lastUsedAt'>;
}

export const MANAGED_GPT_6_SOL_MODEL_ID = 'pi/gpt-6-sol';
export const MANAGED_GPT_56_TERRA_MODEL_ID = 'pi/gpt-5.6-terra';
export const MANAGED_GPT_6_LUNA_MODEL_ID = 'pi/gpt-6-luna';
export const MANAGED_GPT_6_ASTRA_MODEL_ID = 'pi/gpt-6-astra';
export const MANAGED_OPUS_55_MODEL_ID = 'claude-opus-5-5';

const GPT_6_SOL_DEFINITION: ModelDefinition = {
  id: MANAGED_GPT_6_SOL_MODEL_ID,
  name: 'GPT-6 Sol',
  shortName: 'GPT-6 Sol',
  description: 'OpenAI flagship reasoning model',
  provider: 'pi',
  contextWindow: 1_050_000,
  supportsThinking: true,
};

const GPT_56_TERRA_DEFINITION: ModelDefinition = {
  id: MANAGED_GPT_56_TERRA_MODEL_ID,
  name: 'GPT-5.6 Terra',
  shortName: 'GPT-5.6 Terra',
  description: 'OpenAI balanced reasoning model',
  provider: 'pi',
  contextWindow: 1_050_000,
  supportsThinking: true,
};

const GPT_6_LUNA_DEFINITION: ModelDefinition = {
  id: MANAGED_GPT_6_LUNA_MODEL_ID,
  name: 'GPT-6 Luna',
  shortName: 'GPT-6 Luna',
  description: 'OpenAI cost-efficient reasoning model',
  provider: 'pi',
  contextWindow: 1_050_000,
  supportsThinking: true,
};

const GPT_6_ASTRA_DEFINITION: ModelDefinition = {
  id: MANAGED_GPT_6_ASTRA_MODEL_ID,
  name: 'GPT-6 Astra',
  shortName: 'GPT-6 Astra',
  description: 'OpenAI most capable reasoning model',
  provider: 'pi',
  contextWindow: 1_050_000,
  supportsThinking: true,
};

export const MANAGED_MODEL_PRESETS: readonly ManagedModelPreset[] = [
  {
    // Like `fable-5` below, the preset ids and connection slugs are stable keys
    // across model version bumps; only `label` and the model ids move.
    id: 'opus-5',
    label: 'Opus 5.5',
    providerLabel: 'Anthropic',
    connectionSlug: 'origin-anthropic',
    model: MANAGED_OPUS_55_MODEL_ID,
    thinkingLevel: 'think',
    effortLabel: 'high',
    completion: {
      model: MANAGED_OPUS_55_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'high',
    },
    implementation: {
      model: MANAGED_OPUS_55_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'high',
    },
    isDefault: true,
    connection: {
      name: 'Anthropic (Origin)',
      slug: 'origin-anthropic',
      providerType: 'anthropic',
      authType: 'api_key',
      modelSelectionMode: 'automaticallySyncedFromProvider',
      defaultModel: MANAGED_OPUS_55_MODEL_ID,
      isBundled: true,
    },
  },
  {
    id: 'gpt-6-astra',
    label: 'GPT-6 Astra',
    providerLabel: 'OpenAI',
    connectionSlug: 'origin-openai-astra',
    model: MANAGED_GPT_6_ASTRA_MODEL_ID,
    thinkingLevel: 'xhigh',
    effortLabel: 'xhigh',
    completion: {
      model: MANAGED_GPT_6_ASTRA_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'medium',
    },
    implementation: {
      model: MANAGED_GPT_6_LUNA_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'medium',
    },
    connection: {
      name: 'OpenAI Astra (Origin)',
      slug: 'origin-openai-astra',
      providerType: 'pi',
      authType: 'api_key',
      piAuthProvider: 'openai',
      modelSelectionMode: 'userDefined3Tier',
      models: [GPT_6_ASTRA_DEFINITION],
      defaultModel: MANAGED_GPT_6_ASTRA_MODEL_ID,
      isBundled: true,
    },
  },
  {
    id: 'gpt-5.6-sol',
    label: 'GPT-6 Sol',
    providerLabel: 'OpenAI',
    connectionSlug: 'origin-openai',
    model: MANAGED_GPT_6_SOL_MODEL_ID,
    thinkingLevel: 'xhigh',
    effortLabel: 'xhigh',
    completion: {
      model: MANAGED_GPT_56_TERRA_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'medium',
    },
    implementation: {
      model: MANAGED_GPT_6_LUNA_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'medium',
    },
    connection: {
      name: 'OpenAI (Origin)',
      slug: 'origin-openai',
      providerType: 'pi',
      authType: 'api_key',
      piAuthProvider: 'openai',
      modelSelectionMode: 'userDefined3Tier',
      models: [GPT_6_SOL_DEFINITION, GPT_56_TERRA_DEFINITION, GPT_6_LUNA_DEFINITION],
      defaultModel: MANAGED_GPT_6_SOL_MODEL_ID,
      isBundled: true,
    },
  },
  {
    id: 'gpt-5.6-luna',
    label: 'GPT-6 Luna',
    providerLabel: 'OpenAI',
    connectionSlug: 'origin-openai-luna',
    model: MANAGED_GPT_6_LUNA_MODEL_ID,
    thinkingLevel: 'max',
    effortLabel: 'max',
    completion: {
      model: MANAGED_GPT_6_LUNA_MODEL_ID,
      thinkingLevel: 'think',
      effort: 'medium',
    },
    implementation: {
      model: MANAGED_GPT_6_LUNA_MODEL_ID,
      thinkingLevel: 'max',
      effort: 'xhigh',
    },
    connection: {
      name: 'OpenAI Luna (Origin)',
      slug: 'origin-openai-luna',
      providerType: 'pi',
      authType: 'api_key',
      piAuthProvider: 'openai',
      modelSelectionMode: 'userDefined3Tier',
      models: [GPT_6_LUNA_DEFINITION],
      defaultModel: MANAGED_GPT_6_LUNA_MODEL_ID,
      isBundled: true,
    },
  },
  {
    // The preset `id` and `connectionSlug` are stable keys — the slug keys the
    // stored LlmConnection and its encrypted API key, and the id flows through
    // ManagedModelPresetId into the protocol DTO and both model pickers. Only
    // `label` and the model ids move when the Fable version is bumped.
    id: 'fable-5',
    label: 'Fable 5.1',
    providerLabel: 'Anthropic',
    connectionSlug: 'origin-anthropic-fable',
    model: 'claude-fable-5-1',
    thinkingLevel: 'think',
    effortLabel: 'high',
    completion: {
      model: 'claude-fable-5-1',
      thinkingLevel: 'think',
      effort: 'high',
    },
    implementation: {
      model: 'claude-fable-5-1',
      thinkingLevel: 'think',
      effort: 'high',
    },
    connection: {
      name: 'Anthropic Fable (Origin)',
      slug: 'origin-anthropic-fable',
      providerType: 'anthropic',
      authType: 'api_key',
      modelSelectionMode: 'automaticallySyncedFromProvider',
      defaultModel: 'claude-fable-5-1',
      isBundled: true,
    },
  },
] as const;

export function getManagedModelPreset(id: string): ManagedModelPreset | undefined {
  return MANAGED_MODEL_PRESETS.find((preset) => preset.id === id);
}

export function getDefaultManagedModelPreset(): ManagedModelPreset {
  const preset = MANAGED_MODEL_PRESETS.find((candidate) => candidate.isDefault);
  if (!preset) {
    throw new Error('MANAGED_MODEL_PRESETS must include one isDefault preset');
  }
  return preset;
}

export function getManagedModelPresetByConnection(slug: string | undefined): ManagedModelPreset | undefined {
  return MANAGED_MODEL_PRESETS.find((preset) => preset.connectionSlug === slug);
}

export function isManagedLlmConnectionSlug(slug: string): boolean {
  return MANAGED_MODEL_PRESETS.some((preset) => preset.connectionSlug === slug);
}

/** Models a managed connection may use internally without exposing them as primary presets. */
export function getManagedModelIds(preset: ManagedModelPreset): readonly string[] {
  return [...new Set([preset.model, preset.completion.model, preset.implementation.model])];
}

const PROVIDER_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  anthropic: 'Anthropic',
  anthropic_compat: 'Anthropic',
  openai: 'OpenAI',
  'openai-codex': 'OpenAI',
  xai: 'xAI',
  'github-copilot': 'GitHub Copilot',
  google: 'Google',
  openrouter: 'OpenRouter',
  bedrock: 'Amazon Bedrock',
  vertex: 'Google Vertex AI',
};

/**
 * Format the provider/model label shown below the chat input from the active
 * session model and its effective connection.
 */
export function getConnectionModelLabel(
  modelId: string,
  connection?: Pick<LlmConnection, 'slug' | 'name' | 'providerType' | 'piAuthProvider' | 'models'> | null,
): string {
  const managedPreset = MANAGED_MODEL_PRESETS.find((preset) => preset.model === modelId)
    ?? getManagedModelPresetByConnection(connection?.slug);
  const connectionModel = connection?.models?.find((model) =>
    (typeof model === 'string' ? model : model.id) === modelId
  );
  const registeredModel = getModelById(modelId);
  const rawModelName = typeof connectionModel === 'string'
    ? getModelDisplayName(connectionModel)
    : connectionModel?.name ?? registeredModel?.name ?? managedPreset?.label ?? getModelDisplayName(modelId);
  const modelName = rawModelName.replace(/\s*\((?:\d+[KM])\)\s*$/i, '');

  const providerKey = connection?.piAuthProvider
    ?? connection?.providerType
    ?? registeredModel?.provider;
  const providerLabel = managedPreset?.providerLabel
    ?? (providerKey ? PROVIDER_DISPLAY_LABELS[providerKey] : undefined)
    ?? connection?.name
    ?? providerKey;

  if (!providerLabel) return modelName;
  const brandedModelName = providerLabel === 'Anthropic' && !/^Claude\b/i.test(modelName)
    ? `Claude ${modelName}`
    : modelName;
  return `${providerLabel} ${brandedModelName}`;
}

// ============================================================
// Helpers
// ============================================================

/**
 * Get the mini/utility model ID for a connection.
 * Provider-aware search:
 *   - Anthropic (incl. bedrock/vertex): find any model with "haiku" in its id/name
 *   - OpenAI: find any model with "mini" in its id/name
 *   - Otherwise: last model in the list
 *
 * Used for mini agent, title generation, and mini completions.
 *
 * @param connection - LLM connection (or partial with models + providerType)
 * @returns Model ID string, or undefined if no models available
 */
export function getMiniModel(connection: Pick<LlmConnection, 'models' | 'providerType'>): string | undefined {
  return findSmallModel(connection);
}

/**
 * Get the summarization model ID for a connection.
 * Same provider-aware logic as getMiniModel(), but separate
 * so summarization and mini agent models can diverge independently.
 *
 * Used for response summarization and API tool summarization.
 *
 * @param connection - LLM connection (or partial with models + providerType)
 * @returns Model ID string, or undefined if no models available
 */
export function getSummarizationModel(connection: Pick<LlmConnection, 'models' | 'providerType'>): string | undefined {
  return findSmallModel(connection);
}

/**
 * Provider-aware small model resolution.
 * Shared implementation for getMiniModel() and getSummarizationModel().
 *
 *   - Anthropic (incl. bedrock/vertex/compat): find "haiku"
 *   - Pi: find "mini" or "flash"
 *   - Otherwise: last model in the list
 */
function findSmallModel(connection: Pick<LlmConnection, 'models' | 'providerType'>): string | undefined {
  if (!connection.models || connection.models.length === 0) return undefined;

  const toId = (m: ModelDefinition | string) => typeof m === 'string' ? m : m.id;

  const toSearchStr = (m: ModelDefinition | string) =>
    typeof m === 'string' ? m.toLowerCase() : `${m.id} ${m.name} ${m.shortName}`.toLowerCase();

  const isDeniedSmallModel = (modelId: string): boolean => {
    const bare = modelId.startsWith('pi/') ? modelId.slice(3) : modelId;
    return bare === 'codex-mini-latest';
  };

  const isAllowedModel = (m: ModelDefinition | string): boolean => !isDeniedSmallModel(toId(m));

  // Provider-aware keyword search
  const keywords: string[] = [];

  if (isAnthropicProvider(connection.providerType)) {
    keywords.push('haiku');
  } else if (isPiProvider(connection.providerType)) {
    keywords.push('mini', 'flash');
  } else {
    // Aggregator providers (copilot, etc.) — try all common small-model keywords
    keywords.push('mini', 'haiku', 'flash');
  }

  if (keywords.length > 0) {
    const match = connection.models.find(m => {
      if (!isAllowedModel(m)) return false;
      const searchStr = toSearchStr(m);
      return keywords.some(k => searchStr.includes(k));
    });
    if (match) {
      return toId(match);
    }
  }

  // Fallback: last allowed model in the list, otherwise final entry.
  const fallback = [...connection.models].reverse().find(isAllowedModel);
  return fallback ? toId(fallback) : toId(connection.models[connection.models.length - 1]!);
}

/**
 * Generate a URL-safe slug from a display name.
 * @param name - Display name to convert
 * @returns URL-safe slug
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Check if a slug is valid (URL-safe, non-empty).
 * @param slug - Slug to validate
 * @returns true if valid
 */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(slug);
}

/**
 * Get credential key for an LLM connection.
 * Format: llm::{slug}::{credentialType}
 *
 * @param slug - Connection slug
 * @param credentialType - Type of credential ('api_key' or 'oauth_token')
 * @returns Credential key string
 */
export function getLlmCredentialKey(slug: string, credentialType: 'api_key' | 'oauth_token'): string {
  return `llm::${slug}::${credentialType}`;
}

/**
 * Credential storage type for each auth mechanism.
 */
export type LlmCredentialStorageType =
  | 'api_key'           // Single token stored as value
  | 'oauth_token'       // OAuth tokens (access, refresh, expiry)
  | 'iam_credentials'   // AWS-style (accessKeyId, secretAccessKey, region)
  | 'service_account'   // JSON file contents
  | null;               // No storage needed (environment or none)

/**
 * Map LlmAuthType to credential storage type.
 * Determines how credentials are stored in the credential manager.
 *
 * @param authType - LLM auth type
 * @returns Credential storage type or null if no credential storage needed
 */
export function authTypeToCredentialStorageType(authType: LlmAuthType): LlmCredentialStorageType {
  switch (authType) {
    case 'api_key':
    case 'api_key_with_endpoint':
    case 'bearer_token':
      return 'api_key';
    case 'oauth':
      return 'oauth_token';
    case 'iam_credentials':
      return 'iam_credentials';
    case 'service_account_file':
      return 'service_account';
    case 'environment':
    case 'none':
      return null;
  }
}

/**
 * @deprecated Use authTypeToCredentialStorageType instead.
 * Kept for backwards compatibility during migration.
 */
export function authTypeToCredentialType(authType: LlmAuthType): 'api_key' | 'oauth_token' | null {
  const storageType = authTypeToCredentialStorageType(authType);
  if (storageType === 'api_key' || storageType === 'oauth_token') {
    return storageType;
  }
  return null;
}

/**
 * Check if an auth type requires a custom endpoint URL.
 * @param authType - LLM auth type
 * @returns true if endpoint URL field should be shown in UI
 */
export function authTypeRequiresEndpoint(authType: LlmAuthType): boolean {
  return authType === 'api_key_with_endpoint';
}

/**
 * Check if a provider type is a "compat" provider.
 * Compat providers use custom endpoints and require explicit model lists.
 * @param providerType - Provider type to check
 * @returns true if this is a compat provider (anthropic_compat or pi_compat)
 */
export function isCompatProvider(providerType: LlmProviderType): boolean {
  return providerType === 'anthropic_compat' || providerType === 'pi_compat';
}

/**
 * Check if a provider type uses Anthropic models (Claude).
 * Includes direct Anthropic, compat endpoints, and cloud providers (Bedrock, Vertex).
 * @param providerType - Provider type to check
 * @returns true if this provider uses Anthropic/Claude models
 */
export function isAnthropicProvider(providerType: LlmProviderType): boolean {
  return (
    providerType === 'anthropic' ||
    providerType === 'anthropic_compat' ||
    providerType === 'bedrock' ||
    providerType === 'vertex'
  );
}


/**
 * Check if a provider type uses Pi unified API.
 * @param providerType - Provider type to check
 * @returns true if this provider uses Pi
 */
export function isPiProvider(providerType: LlmProviderType): boolean {
  return providerType === 'pi' || providerType === 'pi_compat';
}

/**
 * Get the default model list for a provider type from the registry.
 * For *_compat providers, returns empty array - those should use connection.models instead.
 *
 * @param providerType - Provider type
 * @param piAuthProvider - Optional Pi auth provider for filtering Pi models
 * @returns Model list from registry, or empty array for compat providers
 */
export function getModelsForProviderType(providerType: LlmProviderType, piAuthProvider?: string): ModelDefinition[] {
  // Compat providers require explicit model lists from the connection
  if (isCompatProvider(providerType)) {
    return [];
  }

  // Pi: fetch models via registered resolver (avoids Pi SDK import in renderer)
  if (providerType === 'pi') {
    return _piModelResolver(piAuthProvider);
  }

  // Anthropic, Bedrock, Vertex all use Claude models
  return ANTHROPIC_MODELS;
}

/**
 * Get the default model list for a connection's provider type.
 * Unlike getModelsForProviderType(), this handles compat providers by returning
 * the appropriate compat-prefixed model IDs instead of an empty array.
 *
 * Use this whenever you need to populate or backfill a connection's models.
 *
 * @param providerType - Provider type from the connection
 * @param piAuthProvider - Optional Pi auth provider for filtering Pi models
 * @returns Default model list (ModelDefinition[] for standard, string[] for compat)
 */
/**
 * Preferred default model IDs per Pi auth provider.
 * The Pi SDK returns models in arbitrary order (alphabetical by ID), which means
 * deprecated models like claude-3-5-haiku-20241022 can end up first.
 * This map ensures getDefaultModelForConnection() picks a modern, capable model.
 *
 * Format: bare model IDs (without pi/ prefix). Matched against pi/{id} or pi/{id}-*.
 */
export const PI_PREFERRED_DEFAULTS: Record<string, string[]> = {
  // Keep the first entry in sync with DEFAULT_MODEL (DEFAULT_MODEL_SHORT_NAME in models.ts).
  anthropic: ['claude-opus-5-5', 'claude-opus-5', 'claude-fable-5-1', 'claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
  openai: ['gpt-5.2', 'gpt-5.1', 'gpt-5', 'o4-mini', 'o3', 'gpt-4o'],
  'openai-codex': ['gpt-5.2', 'gpt-5.1', 'gpt-5', 'o4-mini', 'o3', 'gpt-4o'],
  google: ['gemini-3-pro-preview', 'gemini-3-flash-preview', 'gemini-3.1-flash-lite-preview', 'gemini-2.5-pro', 'gemini-2.5-flash'],
  'github-copilot': ['claude-sonnet-4-6', 'gpt-5', 'o4-mini', 'claude-haiku-4-5'],
};

export function getDefaultModelsForConnection(providerType: LlmProviderType, piAuthProvider?: string): Array<ModelDefinition | string> {
  if (providerType === 'pi') {
    const models = _piModelResolver(piAuthProvider);
    // Sort preferred defaults first so getDefaultModelForConnection picks a modern model
    const preferred = (piAuthProvider && PI_PREFERRED_DEFAULTS[piAuthProvider]) || [];
    if (preferred.length > 0) {
      models.sort((a, b) => {
        const aIdx = preferred.findIndex(p => a.id === `pi/${p}` || a.id.startsWith(`pi/${p}-`));
        const bIdx = preferred.findIndex(p => b.id === `pi/${p}` || b.id.startsWith(`pi/${p}-`));
        const aPrio = aIdx >= 0 ? aIdx : preferred.length;
        const bPrio = bIdx >= 0 ? bIdx : preferred.length;
        return aPrio - bPrio;
      });
    }
    return models;
  }
  if (providerType === 'pi_compat') return [];  // Dynamic — user specifies
  if (providerType === 'anthropic_compat') return [];  // Dynamic — user specifies
  // anthropic, bedrock, vertex — DEFAULT_MODEL first so getDefaultModelForConnection()
  // picks it (single source of truth: DEFAULT_MODEL_SHORT_NAME in models.ts).
  const preferred = ANTHROPIC_MODELS.filter(m => m.id === DEFAULT_MODEL);
  const rest = ANTHROPIC_MODELS.filter(m => m.id !== DEFAULT_MODEL);
  return [...preferred, ...rest];
}

/**
 * Get the default model ID for a connection's provider type.
 * Derived from the first entry in getDefaultModelsForConnection() — single source of truth.
 *
 * @param providerType - Provider type from the connection
 * @param piAuthProvider - Optional Pi auth provider for filtering Pi models
 * @returns Default model ID string
 */
export function getDefaultModelForConnection(providerType: LlmProviderType, piAuthProvider?: string): string {
  const models = getDefaultModelsForConnection(providerType, piAuthProvider);
  const first = models[0];
  if (!first) return '';  // Dynamic provider — no default
  return typeof first === 'string' ? first : first.id;
}

/**
 * Resolve the effective LLM connection slug from available fallbacks.
 *
 * Single source of truth for the fallback chain used everywhere in the UI:
 *   1. Explicit session connection (locked after first message)
 *   2. Workspace-level default override
 *   3. Global default (isDefault flag on a connection)
 *   4. First available connection
 *
 * @param sessionConnection  - Per-session connection slug (session.llmConnection)
 * @param workspaceDefault   - Workspace-level default connection slug
 * @param connections        - All available connections (with status metadata)
 * @returns The resolved slug, or undefined when no connections exist
 */
export function resolveEffectiveConnectionSlug(
  sessionConnection: string | undefined,
  workspaceDefault: string | undefined,
  connections: Pick<LlmConnectionWithStatus, 'slug' | 'isDefault'>[],
): string | undefined {
  return sessionConnection
    ?? workspaceDefault
    ?? connections.find(c => c.isDefault)?.slug
    ?? connections[0]?.slug
}

/**
 * Check if a session's locked connection is unavailable (deleted/removed).
 * Returns true only when a session has an explicit llmConnection that doesn't
 * match any current connection. Sessions without a stored connection (using
 * the fallback chain) are never "unavailable".
 *
 * @param sessionConnection - Per-session connection slug (session.llmConnection)
 * @param connections - All available connections
 * @returns true if the session's connection no longer exists
 */
export function isSessionConnectionUnavailable(
  sessionConnection: string | undefined,
  connections: Pick<LlmConnectionWithStatus, 'slug'>[],
): boolean {
  if (!sessionConnection) return false
  return !connections.some(c => c.slug === sessionConnection)
}

/**
 * Check if an auth type uses browser OAuth flow.
 * @param authType - LLM auth type
 * @returns true if OAuth browser flow should be triggered
 */
export function authTypeIsOAuth(authType: LlmAuthType): boolean {
  return authType === 'oauth';
}

/**
 * Check if a provider supports a given auth type.
 * Returns valid combinations for the type system.
 *
 * @param providerType - Provider type
 * @param authType - Auth type to check
 * @returns true if this is a valid combination
 */
export function isValidProviderAuthCombination(
  providerType: LlmProviderType,
  authType: LlmAuthType
): boolean {
  const validCombinations: Record<LlmProviderType, LlmAuthType[]> = {
    anthropic: ['api_key', 'oauth'],
    anthropic_compat: ['api_key_with_endpoint'],
    bedrock: ['bearer_token', 'iam_credentials', 'environment'],
    vertex: ['oauth', 'service_account_file', 'environment'],
    pi: ['api_key', 'oauth', 'none'],
    pi_compat: ['api_key_with_endpoint', 'none'],
  };

  return validCombinations[providerType]?.includes(authType) ?? false;
}

// ============================================================
// Migration Helpers
// ============================================================

/**
 * Migrate legacy connection type to new provider type.
 * Used during config migration.
 *
 * @param legacyType - Legacy LlmConnectionType value
 * @returns New LlmProviderType value
 */
export function migrateConnectionType(legacyType: LlmConnectionType): LlmProviderType {
  switch (legacyType) {
    case 'anthropic':
      return 'anthropic';
    case 'openai':
      return 'pi';
    case 'openai-compat':
      return 'pi_compat';
  }
}

/**
 * Migrate legacy auth type to new auth type.
 * Determines new auth type based on legacy type + connection context.
 *
 * @param legacyAuthType - Legacy auth type ('api_key' | 'oauth' | 'none')
 * @param hasCustomEndpoint - Whether connection has a custom baseUrl
 * @returns New LlmAuthType value
 */
export function migrateAuthType(
  legacyAuthType: 'api_key' | 'oauth' | 'none',
  hasCustomEndpoint: boolean
): LlmAuthType {
  switch (legacyAuthType) {
    case 'api_key':
      // If has custom endpoint, use api_key_with_endpoint
      return hasCustomEndpoint ? 'api_key_with_endpoint' : 'api_key';
    case 'oauth':
      return 'oauth';
    case 'none':
      return 'none';
  }
}

// ============================================================
// Auth Environment Variable Resolution
// ============================================================

/**
 * Result of resolving auth env vars for an LLM connection.
 */
export interface ResolvedAuthEnvVars {
  /** Environment variables to set (e.g., ANTHROPIC_API_KEY, CLAUDE_CODE_OAUTH_TOKEN) */
  envVars: Record<string, string>;
  /** Whether credentials were successfully resolved */
  success: boolean;
  /** Warning message if auth resolution encountered issues */
  warning?: string;
}

/**
 * Resolve authentication environment variables for an LLM connection.
 *
 * Provider-agnostic: switches on providerType to determine which env vars
 * to set and how to retrieve credentials. Shared by:
 * - `SessionManager.reinitializeAuth()` (applies to process.env)
 * - `ClaudeAgent.postInit()` (applies to process.env + envOverrides)
 *
 * Providers that handle auth internally (openai, copilot, pi) return
 * empty envVars — their auth is managed in postInit() via native mechanisms.
 *
 * @param connection - The LLM connection config
 * @param connectionSlug - Connection slug for credential lookup
 * @param credentialManager - Credential manager instance
 * @param getValidOAuthToken - Function to get a valid (refreshed) OAuth token
 * @returns Resolved env vars and status
 */
export async function resolveAuthEnvVars(
  connection: LlmConnection,
  connectionSlug: string,
  credentialManager: CredentialManager,
  getValidOAuthToken: (slug: string) => Promise<{ accessToken?: string | null }>,
): Promise<ResolvedAuthEnvVars> {
  const envVars: Record<string, string> = {};

  // Only Anthropic-SDK-based providers use env var auth
  // OpenAI (Codex), Copilot, and Pi handle auth internally in their postInit()
  if (!isAnthropicProvider(connection.providerType)) {
    return { envVars, success: true };
  }

  // Set base URL if configured
  if (connection.baseUrl) {
    envVars.ANTHROPIC_BASE_URL = connection.baseUrl;
  }

  const authType = connection.authType;

  if (authType === 'api_key' || authType === 'api_key_with_endpoint' || authType === 'bearer_token') {
    const apiKey = await credentialManager.getLlmApiKey(connectionSlug);
    if (apiKey) {
      envVars.ANTHROPIC_API_KEY = apiKey;
    } else if (connection.baseUrl) {
      // Keyless provider (e.g. Ollama)
      envVars.ANTHROPIC_API_KEY = 'not-needed';
    } else {
      return { envVars, success: false, warning: `No API key found for: ${connectionSlug}` };
    }
  } else if (authType === 'oauth') {
    if (connection.providerType === 'anthropic') {
      // Anthropic OAuth uses getValidClaudeOAuthToken which handles token refresh
      const tokenResult = await getValidOAuthToken(connectionSlug);
      if (tokenResult.accessToken) {
        envVars.CLAUDE_CODE_OAUTH_TOKEN = tokenResult.accessToken;
      } else {
        return { envVars, success: false, warning: `Failed to get OAuth token for: ${connectionSlug}` };
      }
    } else {
      // Non-Anthropic OAuth (e.g. anthropic_compat with OAuth)
      const llmOAuth = await credentialManager.getLlmOAuth(connectionSlug);
      if (llmOAuth?.accessToken) {
        envVars.CLAUDE_CODE_OAUTH_TOKEN = llmOAuth.accessToken;
      } else {
        return { envVars, success: false, warning: `No OAuth token found for: ${connectionSlug}` };
      }
    }
  } else if (authType === 'environment') {
    // Environment auth — credentials come from process.env, nothing to inject
    return { envVars, success: true };
  }

  return { envVars, success: true };
}

/**
 * Migrate a legacy LlmConnection to the new format.
 * Creates a new connection object with providerType instead of type.
 *
 * @param legacy - Legacy connection with 'type' field
 * @returns Migrated connection with 'providerType' field
 */
export function migrateLlmConnection(legacy: {
  slug: string;
  name: string;
  type: LlmConnectionType;
  baseUrl?: string;
  authType: 'api_key' | 'oauth' | 'none';
  models?: ModelDefinition[];
  defaultModel?: string;
  createdAt: number;
  lastUsedAt?: number;
}): LlmConnection {
  const providerType = migrateConnectionType(legacy.type);
  const hasCustomEndpoint = !!legacy.baseUrl && legacy.type !== 'anthropic';
  const authType = migrateAuthType(legacy.authType, hasCustomEndpoint);

  return {
    slug: legacy.slug,
    name: legacy.name,
    providerType,
    type: legacy.type, // Keep for backwards compatibility
    baseUrl: legacy.baseUrl,
    authType,
    models: legacy.models,
    defaultModel: legacy.defaultModel,
    createdAt: legacy.createdAt,
    lastUsedAt: legacy.lastUsedAt,
  };
}
